import { useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Crown, Download, Sparkles, X } from "lucide-react";
import { Eyebrow, Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FREE_RESOURCES_URL = "https://www.learnwithbailey.org/free-resources";

const FREE = [
  { name: "Yearly Birthday Calendar", blurb: "A colourful year-at-a-glance calendar so no birthday is ever forgotten.", file: "/downloads/yearly-birthday-calendar.pdf", local: true, thumb: "/downloads/thumbs/yearly-birthday-calendar.png" },
  { name: "Reading Achievement Award", blurb: "The official Bailey Adventure Books award for brilliant readers.", file: "/downloads/reading-achievement-award.pdf", local: true, thumb: "/downloads/thumbs/reading-achievement-award.png", price: 1 },
  { name: "Bailey Certificate", blurb: "A printable Bailey certificate to celebrate any win, big or small.", file: "/downloads/bailey-certificate.pdf", local: true, thumb: "/downloads/thumbs/bailey-certificate.png", price: 1 },
  { name: "10 Reasons to Read", blurb: "A printable poster sharing ten brilliant reasons to pick up a book.", file: "/downloads/10-reasons-to-read.pdf", local: true, thumb: "/downloads/thumbs/10-reasons-to-read.png" },
  { name: "Bailey's List — What Can You Do?", blurb: "A tick-off list of things little ones can do, inspired by Bailey's adventures.", file: "/downloads/baileys-list-what-can-you-do.pdf", local: true, thumb: "/downloads/thumbs/baileys-list-what-can-you-do.png" },
  { name: "Bailey in London Snakes & Ladders", blurb: "Climb the landmarks and slide past the buses — a London-themed game to download and print.", file: "/downloads/london-snakes-and-ladders.pdf", local: true, thumb: "/downloads/thumbs/london-snakes-and-ladders.png", price: 1 },
  { name: "Affirmations Colour & Trace", blurb: "Twelve colour-in and tracing worksheets to help little ones learn their affirmations.", file: "/downloads/affirmations-colour-trace.pdf", local: true, thumb: "/downloads/thumbs/affirmations-colour-trace.png", price: 3.5 },
  { name: "Colouring Sheets", blurb: "Eight Bailey, What Can You Do? colouring pages for creative young artists.", file: "/downloads/colouring-page.pdf", local: true, thumb: "/downloads/thumbs/colouring-page.png" },
  { name: "Word Search", blurb: "A puzzle packed with Bailey-themed vocabulary.", file: "/downloads/word-search.pdf", local: true, thumb: "/downloads/thumbs/word-search.png" },
  { name: "Parent's Reading Guide", blurb: "A 14-page guide to making the most of reading time together.", file: "/downloads/parents-reading-guide.pdf", local: true, thumb: "/downloads/thumbs/parents-reading-guide.png" },
  { name: "Weekly Planner", blurb: "A colourful day-by-day family planner, from 7am to 7pm.", file: "/downloads/weekly-planner.pdf", local: true, thumb: "/downloads/thumbs/weekly-planner.png" },
  { name: "Bailey Starts School — Trace Numbers 1-10", blurb: "Trace the numbers 1 to 10 with Bailey — start at the green dot, finger first, then pencil.", file: "/downloads/starts-school-trace-numbers.pdf", local: true, thumb: "/downloads/thumbs/starts-school-trace-numbers.png" },
  { name: "Bailey Starts School GIFT", blurb: "A free gift sheet — circle your favourite first-day activities, colour a feeling and draw yourself at school.", file: "/downloads/starts-school-gift.pdf", local: true, thumb: "/downloads/thumbs/starts-school-gift.png" },
];

const PREMIUM = [
  { name: "Premium activity packs", blurb: "Themed worksheet bundles that extend each Bailey adventure." },
  { name: "Structured lesson plans", blurb: "Classroom-ready plans designed by educators for early years." },
  { name: "Premium certificates", blurb: "Beautifully designed keepsake certificates for big milestones." },
];

export default function Worksheets() {
  const [order, setOrder] = useState(null);
  const [buyer, setBuyer] = useState({ name: "", email: "" });
  const [sending, setSending] = useState(false);

  const scrollToNewsletter = () => {
    const el = document.getElementById("team-bailey");
    if (el) window.__lenis ? window.__lenis.scrollTo(el, { offset: -80 }) : el.scrollIntoView({ behavior: "smooth" });
  };

  const submitOrder = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        name: buyer.name,
        email: buyer.email,
        message: `£${order.price.toFixed(2)} download order: ${order.name}`,
        type: "worksheet-order",
      });
      toast.success("Order sent — Damali will email you payment and your download.");
      setOrder(null);
      setBuyer({ name: "", email: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const cardInner = (w) => (
    <>
      {w.thumb ? (
        <div className="relative h-44 bg-creamdark border-b border-sand overflow-hidden">
          <img
            src={w.thumb}
            alt={`Preview of ${w.name}`}
            loading="lazy"
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
          {w.price ? (
            <span className="absolute top-3 right-3 text-[11px] font-bold uppercase tracking-widest text-cream bg-amberbrand rounded-full px-3 py-1 shadow-sm">
              £{w.price.toFixed(2)}
            </span>
          ) : (
            <span className="absolute top-3 right-3 text-[10px] font-bold uppercase tracking-widest text-sage bg-cream/95 border border-sage/25 rounded-full px-2.5 py-1 shadow-sm">
              Instant PDF
            </span>
          )}
        </div>
      ) : (
        <div className="h-44 bg-gradient-to-br from-creamdark to-sagecream border-b border-sand flex items-center justify-center">
          <span className="inline-flex w-14 h-14 rounded-2xl bg-terracotta/15 items-center justify-center group-hover:bg-terracotta/25 group-hover:scale-110 transition-all duration-300">
            <Download size={24} className="text-terracotta" />
          </span>
        </div>
      )}
      <div className="flex flex-col flex-1 p-6">
        <h3 className="font-display font-bold text-lg tracking-tight text-walnut mb-1.5">{w.name}</h3>
        <p className="text-sm text-walnutsoft leading-relaxed mb-5">{w.blurb}</p>
        <span className="mt-auto inline-flex items-center gap-2 text-sm font-display font-bold text-terracotta group-hover:gap-3 transition-all">
          {w.price ? <>Buy download — £{w.price.toFixed(2)} <ArrowRight size={15} /></> : <>Download free PDF <ArrowRight size={15} /></>}
        </span>
      </div>
    </>
  );

  const cardClass = "group flex flex-col h-full w-full text-left bg-white/80 border border-sand rounded-3xl overflow-hidden hover:-translate-y-1.5 hover:shadow-[0_24px_44px_-20px_rgba(49,67,92,0.3)] transition-all duration-300";

  return (
    <section id="worksheets" data-testid="worksheets-section" className="py-24 sm:py-32 bg-creamdark/60 border-y border-sand">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-14">
          <div>
            <Eyebrow className="mb-4">Learn & play</Eyebrow>
            <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-walnut">
              Worksheets & printables
            </h2>
          </div>
          <p className="max-w-sm text-sm sm:text-base text-walnutsoft leading-relaxed">
            Free Bailey activities plus premium downloads for just £1 — print at home
            or use in the classroom.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 mb-20">
          {FREE.map((w, i) => (
            <Reveal key={w.name} delay={(i % 4) * 0.07}>
              {w.price ? (
                <button
                  type="button"
                  onClick={() => setOrder(w)}
                  data-testid={`worksheet-${w.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className={cardClass}
                >
                  {cardInner(w)}
                </button>
              ) : (
                <a
                  href={w.file}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`worksheet-${w.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className={cardClass}
                >
                  {cardInner(w)}
                </a>
              )}
            </Reveal>
          ))}
          <Reveal delay={0.2}>
            <div className="flex flex-col justify-between h-full rounded-3xl bg-sage text-cream p-6">
              <p className="font-display font-bold text-xl leading-snug">Most downloads are free — forever.</p>
              <p className="text-sm text-cream/75 mt-3">Premium picks are just £1. Print as many as you like for home or classroom use.</p>
            </div>
          </Reveal>
        </div>

        <Reveal className="mb-8">
          <div className="flex items-center gap-3">
            <Crown size={22} className="text-amberbrand" />
            <h3 className="font-display font-bold tracking-tight text-xl sm:text-2xl text-walnut">Premium worksheets</h3>
            <span data-testid="premium-coming-soon-badge" className="text-[11px] font-bold uppercase tracking-widest text-amberbrand bg-amberbrand/15 rounded-full px-3 py-1">
              Paid · Coming soon
            </span>
          </div>
          <p className="mt-3 max-w-xl text-sm sm:text-base text-walnutsoft leading-relaxed">
            Exclusive learning packs designed by educators are being prepared right now.
            Join Team Bailey to be first to know when the premium store opens.
          </p>
        </Reveal>

        <div className="grid sm:grid-cols-3 gap-5">
          {PREMIUM.map((p, i) => (
            <Reveal key={p.name} delay={i * 0.08}>
              <div
                data-testid={`premium-card-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="relative h-full rounded-3xl border-2 border-dashed border-amberbrand/40 bg-white/60 p-7"
              >
                <span className="inline-flex w-11 h-11 rounded-2xl bg-amberbrand/15 items-center justify-center mb-4">
                  <Sparkles size={19} className="text-amberbrand" />
                </span>
                <h4 className="font-display font-bold text-lg tracking-tight text-walnut mb-1.5">{p.name}</h4>
                <p className="text-sm text-walnutsoft leading-relaxed mb-6">{p.blurb}</p>
                <button
                  data-testid={`premium-waitlist-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  onClick={scrollToNewsletter}
                  className="inline-flex items-center gap-2 rounded-full border-2 border-walnut text-walnut px-5 py-2.5 text-sm font-display font-bold hover:bg-walnut hover:text-cream transition-colors"
                >
                  Join the waitlist <ArrowRight size={14} />
                </button>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {order && (
          <>
            <motion.div
              data-testid="order-modal-backdrop"
              className="fixed inset-0 z-[95] bg-walnut/40 backdrop-blur-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOrder(null)}
            />
            <motion.div
              data-testid="order-modal"
              className="fixed z-[100] inset-x-4 top-1/2 -translate-y-1/2 sm:inset-x-0 sm:mx-auto max-w-md bg-cream border border-sand rounded-3xl p-7 sm:p-8 shadow-2xl"
              initial={{ opacity: 0, y: 30, scale: 0.96 }}
              animate={{ opacity: 1, y: "-50%", scale: 1 }}
              exit={{ opacity: 0, y: 30, scale: 0.96 }}
              transition={{ type: "spring", damping: 26, stiffness: 260 }}
            >
              <div className="flex items-start justify-between gap-4 mb-1.5">
                <h3 className="font-display font-bold text-xl tracking-tight text-walnut">{order.name}</h3>
                <button
                  data-testid="order-modal-close"
                  onClick={() => setOrder(null)}
                  className="w-8 h-8 shrink-0 rounded-full border border-sand flex items-center justify-center text-walnutsoft hover:text-walnut transition-colors"
                  aria-label="Close"
                >
                  <X size={15} />
                </button>
              </div>
              <p className="text-sm text-walnutsoft mb-6">
                <span className="font-display font-extrabold text-2xl text-walnut">£{order.price.toFixed(2)}</span>
                <span className="ml-2">instant download after payment</span>
              </p>
              <form data-testid="order-form" onSubmit={submitOrder} className="flex flex-col gap-3">
                <input
                  data-testid="order-name-input"
                  required
                  value={buyer.name}
                  onChange={(e) => setBuyer({ ...buyer, name: e.target.value })}
                  placeholder="Your name"
                  className="w-full rounded-xl border border-sand bg-white/80 px-4 py-3 text-sm text-walnut placeholder:text-walnutmuted focus:outline-none focus:ring-2 focus:ring-terracotta/50 focus:border-terracotta transition"
                />
                <input
                  data-testid="order-email-input"
                  required
                  type="email"
                  value={buyer.email}
                  onChange={(e) => setBuyer({ ...buyer, email: e.target.value })}
                  placeholder="Your email address"
                  className="w-full rounded-xl border border-sand bg-white/80 px-4 py-3 text-sm text-walnut placeholder:text-walnutmuted focus:outline-none focus:ring-2 focus:ring-terracotta/50 focus:border-terracotta transition"
                />
                <button
                  data-testid="order-submit-button"
                  type="submit"
                  disabled={sending}
                  className="mt-2 rounded-full bg-terracotta text-cream py-3.5 font-display font-bold text-sm flex items-center justify-center gap-2 hover:bg-terracottadark transition-colors disabled:opacity-60"
                >
                  {sending ? "Sending…" : `Place order — £${order.price.toFixed(2)}`} <ArrowRight size={15} />
                </button>
                <p className="text-xs text-walnutmuted text-center">Damali emails you a secure payment link and your file.</p>
              </form>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </section>
  );
}

